import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader } from "./adminShared";
import { GrantFeaturedPickerModal } from "./AdminCompModals";
import { Star, Loader2, Gift, Check, X, Clock, Eye, StarOff } from "lucide-react";

const FSTATUS = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved · awaiting payment", cls: "bg-blue-100 text-blue-700" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  expired: { label: "Expired", cls: "bg-slate-100 text-slate-500" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-600" },
  removed: { label: "Removed", cls: "bg-slate-100 text-slate-500" },
};

const fdate = (s) => (s ? String(s).slice(0, 10) : "—");

export default function AdminFeatured() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/featured").then(({ data }) => setRows(data.featured || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setActing(r.id);
    try { await api.post(`/admin/featured/requests/${r.id}/approve`); toast.success(`Approved — ${r.name} can now pay to activate`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setActing(null); }
  };
  const reject = async (r) => {
    const reason = window.prompt(`Reject the Featured request for "${r.name}"?\n\nEnter a reason (sent to the organizer):`);
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A rejection reason is required");
    setActing(r.id);
    try { await api.post(`/admin/featured/requests/${r.id}/reject`, { reason: reason.trim() }); toast.success("Request rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setActing(null); }
  };
  const removeFeatured = async (r) => {
    if (!window.confirm("Remove this race from Featured Races? The race listing will remain available as a normal race if published. This action will not delete the race.")) return;
    setActing(r.id);
    try { await api.delete(`/admin/featured/${r.id}`); toast.success("Removed from Featured — race listing kept"); load(); }
    catch { toast.error("Failed"); }
    finally { setActing(null); }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const others = rows.filter((r) => r.status !== "pending");

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <AdminHeader title="Featured Races" subtitle="Review organizer Featured requests and grant complimentary promotions." />
        <button onClick={() => setPicker(true)} data-testid="grant-free-featured-btn"
          className="inline-flex items-center gap-2 bg-amber-500 text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-amber-600 shrink-0">
          <Gift size={15} /> Grant Free Featured Race
        </button>
      </div>

      {/* Pending Featured Requests */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="pending-featured-requests">
        <h3 className="font-display font-bold uppercase text-lg text-slate-800 mb-1 flex items-center gap-2">
          <Clock size={18} className="text-amber-500" /> Pending Featured Requests
          <span className="ml-1 text-[11px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid="pending-featured-count">{pending.length}</span>
        </h3>
        <p className="text-sm text-slate-500 mb-4">Organizer requests awaiting your review. Separate from pending race listing submissions.</p>
        {loading ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" /></div> :
          pending.length === 0 ? <p className="text-slate-500 text-sm py-6 text-center">No pending Featured requests. All caught up! 🎉</p> : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="text-slate-400 text-xs uppercase"><tr>
                <th className="text-left pb-2">Race</th><th className="text-left pb-2">Organizer</th><th className="text-left pb-2">Requested</th><th className="text-left pb-2">Duration</th><th className="text-right pb-2">Actions</th>
              </tr></thead>
              <tbody>{pending.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`featured-request-${r.id}`}>
                  <td className="py-3 font-semibold text-slate-800">{r.name}</td>
                  <td className="py-3 text-slate-600">{r.organizer_name}</td>
                  <td className="py-3 text-slate-500">{fdate(r.request_date)}</td>
                  <td className="py-3 text-slate-600">{r.requested_months ? `${r.requested_months} mo` : "—"}</td>
                  <td className="py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Link to={`/admin/races/${r.id}`} className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-slate-50" data-testid={`featured-review-${r.id}`}><Eye size={13} /> Review</Link>
                      <button onClick={() => approve(r)} disabled={acting === r.id} data-testid={`featured-approve-${r.id}`}
                        className="inline-flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-emerald-600 disabled:opacity-40">{acting === r.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve</button>
                      <button onClick={() => reject(r)} disabled={acting === r.id} data-testid={`featured-reject-${r.id}`}
                        className="inline-flex items-center gap-1 border border-red-200 text-red-600 text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-40"><X size={13} /> Reject</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
      </div>

      {/* All other featured (active / approved / expired / rejected) */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="featured-history">
        <h3 className="font-display font-bold uppercase text-lg text-slate-800 mb-4 flex items-center gap-2"><Star size={18} className="text-amber-500" /> Featured Races</h3>
        {loading ? null : others.length === 0 ? <p className="text-slate-500 text-sm py-6 text-center">No active or past Featured Races yet.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="text-slate-400 text-xs uppercase"><tr>
              <th className="text-left pb-2">Race</th><th className="text-left pb-2">Organizer</th><th className="text-left pb-2">Status</th><th className="text-left pb-2">Period</th><th className="text-right pb-2">Payment</th><th className="text-right pb-2"></th>
            </tr></thead>
            <tbody>{others.map((r) => { const st = FSTATUS[r.status] || FSTATUS.expired; return (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`featured-row-${r.id}`}>
                <td className="py-3 font-semibold text-slate-800"><Link to={`/admin/races/${r.id}`} className="hover:underline">{r.name}</Link></td>
                <td className="py-3 text-slate-600">{r.organizer_name}</td>
                <td className="py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span></td>
                <td className="py-3 text-slate-500">{r.featured_start || r.featured_end ? `${fdate(r.featured_start)} → ${fdate(r.featured_end)}` : "—"}</td>
                <td className="py-3 text-right text-slate-600">{r.amount_paid ? `RM${r.amount_paid}` : <span className="text-slate-400">—</span>}</td>
                <td className="py-3 text-right"><button onClick={() => removeFeatured(r)} disabled={acting === r.id} data-testid={`featured-remove-${r.id}`} title="Remove from Featured" className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-600 text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 disabled:opacity-40">{acting === r.id ? <Loader2 size={13} className="animate-spin" /> : <StarOff size={13} />} Remove from Featured</button></td>
              </tr>
            ); })}</tbody>
          </table></div>
        )}
      </div>

      {picker && <GrantFeaturedPickerModal onClose={() => setPicker(false)} onDone={load} />}
    </div>
  );
}
